/**
 * The word a reader pressed, for Meaning (ADR-0141).
 *
 * Found with `Intl.Segmenter`, which follows Unicode's word boundaries, never
 * with `\b`: that is ASCII-only and fires inside Persian words, and a ZWNJ is
 * part of a Persian word (CLAUDE.md).
 */

/** The longest word Meaning asks about; the server's bound, in runes. */
export const MAX_WORD_CHARS = 40;

const LETTER = /[\p{L}\p{N}]/u;
const EDGES = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}\p{M}]+$/gu;

/** `raw` without the punctuation around it, or undefined if no word is left. */
export function cleanWord(raw: string): string | undefined {
  const word = raw.trim().replace(EDGES, "");
  if (!word || !LETTER.test(word) || [...word].length > MAX_WORD_CHARS) return undefined;
  return word;
}

/** The word holding `offset` in `text`, or the one just before it. */
export function wordIn(text: string, offset: number): string | undefined {
  const segments = [...new Intl.Segmenter(undefined, { granularity: "word" }).segment(text)];
  for (const at of [offset, offset - 1]) {
    const s = segments.find((g) => at >= g.index && at < g.index + g.segment.length);
    if (s?.isWordLike) return cleanWord(s.segment);
  }
  return undefined;
}

/** The word under a point on screen, or undefined off text. */
export function wordAtPoint(x: number, y: number): string | undefined {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  let node: Node | undefined;
  let offset = 0;
  const position = doc.caretPositionFromPoint?.(x, y);
  if (position) {
    node = position.offsetNode;
    offset = position.offset;
  } else {
    const range = doc.caretRangeFromPoint?.(x, y);
    if (range) {
      node = range.startContainer;
      offset = range.startOffset;
    }
  }
  if (!node || node.nodeType !== Node.TEXT_NODE) return undefined;
  return wordIn(node.textContent ?? "", offset);
}
